import SeeMore from 'ui/modules/Pagination/SeeMore';
import Update from './Update';
import React from 'react';
import { communityUrls } from 'urls';
import { TinyCommunity } from 'types/company/community';
import MasonryGrid from 'ui/layout/masonry/MasonryGrid';
import useCommunityRole from 'hooks/useCommunityRole';
import { UpdatesFilter } from 'actions/content/updates';
import Resource from 'util/resource/Resource';
import { useCommunityUpdates } from 'domain/Updates/community/useCommunityUpdates';
import { PaginatedResult, PaginationOptions } from 'types/api';
import { getOrUndefined, IResource } from 'util/resource';

import { halfSpacing } from 'ui/theme/themeConstants';
import styled from '@emotion/styled';
import { CommunityUpdate } from 'types/content';
import { useMediaQuery } from '@mui/material';
import { bluePlanetTheme } from 'ui/theme';
import classNames from 'classnames';
import { useRouteMatch } from 'react-router';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { MobileMargin } from 'ui/views/layouts/BaseLayout';
import TopicSelector from '../../../../domain/Updates/community/TopicSelector';
import Card from 'ui/views/cards/Card';
import useWindowWidth from 'hooks/useWindowWidth';
import { PostedBy } from 'domain/Updates/community/utils';

const UpdateListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${halfSpacing};
`;

function ListWrapper({ useMasonry, children }: { useMasonry?: boolean; children: React.ReactElement }) {
  if (useMasonry) {
    return <MasonryGrid data-intercom-target="community-feed-widget">{children}</MasonryGrid>;
  }
  return <UpdateListContainer data-intercom-target="community-feed-widget">{children}</UpdateListContainer>;
}

export default function UpdateList(props: {
  community: TinyCommunity;
  filter: UpdatesFilter;
  onChange: () => void;
  paginationOptions: PaginationOptions;
  loadPage?: (page: number) => void;
  showSeeMore?: boolean;
  useMasonry?: boolean;
  shouldDisplayTopicSelector: boolean;
}) {
  const queryParams = new URLSearchParams(location.search);
  const topicId = queryParams.get('topicId');
  const isMobile = useMediaQuery(bluePlanetTheme.breakpoints.down('sm'));

  const { resource } = useCommunityUpdates(
    props.community.id,
    props.paginationOptions,
    props.filter,
    topicId ?? undefined,
  );
  return (
    <Resource resource={resource}>
      {updates =>
        updates.total === 0 && updates.page === 1 ? (
          <MobileMargin>
            <ListWrapper useMasonry={props.useMasonry}>
              <>
                {!isMobile && (
                  <Card>
                    <p className="text-small text-italic">No updates have been posted yet</p>
                  </Card>
                )}
                {props.shouldDisplayTopicSelector && (
                  <TopicSelector sectionToOpen="updates" community={props.community} />
                )}
                {isMobile && (
                  <Card>
                    <p className="text-small text-italic">No updates have been posted yet</p>
                  </Card>
                )}
              </>
            </ListWrapper>
          </MobileMargin>
        ) : (
          <List {...props} updates={updates} shouldHidePinnedPosts={false} />
        )
      }
    </Resource>
  );
}

export function UpdateListPreloaded(props: {
  resource: IResource<PaginatedResult<CommunityUpdate>>;
  community: TinyCommunity;
  onChange: () => void;
  paginationOptions: { page: number; limit: number };
  loadPage?: (page: number) => void;
  showSeeMore?: boolean;
  useMasonry?: boolean;
  shouldHideLoading?: boolean;
  shouldHidePinnedPosts?: boolean;
  shouldDisplayTopicSelector: boolean;
}) {
  const { resource, paginationOptions } = props;

  return (
    <Resource resource={resource} renderLoading={props.shouldHideLoading ? 'Nothing' : undefined}>
      {updates => {
        const start = (paginationOptions.page - 1) * paginationOptions.limit;
        const end = paginationOptions.page * paginationOptions.limit;

        return (
          <List
            {...props}
            shouldHidePinnedPosts={props.shouldHidePinnedPosts ?? false}
            updates={{ ...updates, values: updates.values.slice(start, end) }}
          />
        );
      }}
    </Resource>
  );
}

export function getPostedBy(
  update: CommunityUpdate,
  viewCompanyHref: (companyId: number) => string,
): PostedBy {
  if (update.postedBy.type === 'community') {
    return { type: 'community' };
  } else if (update.postedBy.type === 'user') {
    return { type: 'user', userProfile: update.postedBy.user };
  } else {
    return {
      type: 'company',
      company: update.postedBy.company,
      viewCompanyHref: update.postedBy.company ? viewCompanyHref(update.postedBy.company.id) : undefined,
    };
  }
}

function List(props: {
  updates: PaginatedResult<CommunityUpdate>;
  community: TinyCommunity;
  onChange: () => void;
  shouldHidePinnedPosts: boolean;
  loadPage?: (page: number) => void;
  showSeeMore?: boolean;
  useMasonry?: boolean;
  shouldDisplayTopicSelector: boolean;
}) {
  const { community, updates, shouldHidePinnedPosts } = props;

  const width = useWindowWidth();
  const isSmallScreen = width < bluePlanetTheme.breakpoints.values.md;
  const isMobile = width < bluePlanetTheme.breakpoints.values.sm;

  const { hasScope, myCompanyMemberships } = useCommunityRole(community.slug);
  const { resource: userProfileResource } = useSelfUserProfile();
  const me = getOrUndefined(userProfileResource);

  const match = useRouteMatch();
  const withoutFirstPinnedUpdates = shouldHidePinnedPosts
    ? updates.values.filter(
        (communityUpdate, index) =>
          !((index === 0 || index === 1) && !communityUpdate.update.archived && communityUpdate.isPinned),
      )
    : updates.values;

  if (shouldHidePinnedPosts && withoutFirstPinnedUpdates.length === 0) {
    return (
      <MobileMargin>
        <Card>
          <p className="text-small text-italic">No updates have been posted yet</p>
        </Card>
      </MobileMargin>
    );
  }

  return (
    <div className="u-section-spacing-bottom">
      <ListWrapper useMasonry={props.useMasonry}>
        <>
          {withoutFirstPinnedUpdates.map((update, index) => {
            const canEditPost =
              update.postedBy.type === 'community'
                ? hasScope('Write', 'Update', 'Admin')
                : update.postedBy.type === 'company'
                  ? myCompanyMemberships.some(
                      m => update.postedBy.type === 'company' && m.company.id === update.postedBy.company?.id,
                    )
                  : update.postedBy.type === 'user'
                    ? update.postedBy.user?.cwUserId === me?.cwUserId
                    : false;

            const shouldShowTopicSelector = props.shouldDisplayTopicSelector && updates.page === 1 && index === 0;

            return (
              <React.Fragment key={`update-preview-${update.update.id}`}>
                {shouldShowTopicSelector && isSmallScreen && (
                  <TopicSelector sectionToOpen="updates" community={community} />
                )}
                <Update
                  update={update}
                  creator={update.update.createdBy}
                  community={community}
                  postedBy={getPostedBy(update, companyId => communityUrls.companies.view(match.url, companyId, 'community-feed'))}
                  shownIn="community"
                  onEdit={props.onChange}
                  onArchive={props.onChange}
                  onDelete={props.onChange}
                  canEditPost={canEditPost}
                  canPinToFeed={hasScope('Write', 'Update', 'Admin') && update.postedBy.type === 'community'}
                />
                {shouldShowTopicSelector && !isSmallScreen && (
                  <TopicSelector sectionToOpen="updates" community={community} />
                )}
              </React.Fragment>
            );
          })}
        </>
      </ListWrapper>
      {props.showSeeMore && (
        <SeeMore
          className={classNames('u-content-spacing-top', { ['u-content-spacing-left']: isMobile })}
          resource={updates}
          loadResource={opts => props.loadPage && props.loadPage(opts.page || 1)}
          limit={updates.limit}
        />
      )}
    </div>
  );
}
